import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Calendar, User, Tag, ArrowLeft, ArrowRight, Clock } from 'lucide-react';
import { blogPosts } from '../data/mock';
import Layout from '../components/layout/Layout';

const BlogPage = () => {
  const { id } = useParams();

  // Single post view
  if (id) {
    const post = blogPosts.find(p => p.id === parseInt(id));
    
    if (!post) {
      return (
        <Layout>
          <div className="min-h-[60vh] flex items-center justify-center">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-gray-700 mb-4">Artículo no encontrado</h1>
              <Link to="/blog" className="text-[#1E40AF] hover:underline">Volver al blog</Link>
            </div>
          </div>
        </Layout>
      );
    }

    const relatedPosts = blogPosts.filter(p => p.id !== post.id).slice(0, 2);

    return (
      <Layout>
        {/* Hero */}
        <section className="relative py-16 bg-[#0A1628]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="flex items-center gap-2 text-sm text-gray-400 mb-6">
              <Link to="/" className="hover:text-white transition-colors">Inicio</Link>
              <span>/</span>
              <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
              <span>/</span>
              <span className="text-blue-400 truncate">{post.title}</span>
            </nav>
            <span className="inline-block px-3 py-1 bg-[#1E40AF] text-white text-sm rounded-full mb-4">
              {post.category}
            </span>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-gray-400 text-sm">
              <span className="flex items-center gap-2">
                <User className="h-4 w-4" />
                {post.author}
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {post.date}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                5 min de lectura
              </span>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <img
              src={post.image}
              alt={post.title}
              className="w-full aspect-video object-cover rounded-2xl mb-8"
            />
            <div className="prose prose-lg max-w-none">
              <p className="text-xl text-gray-600 leading-relaxed mb-8 font-medium">
                {post.excerpt}
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                {post.content}
              </p>
              <h2 className="text-2xl font-bold text-[#0A1628] mt-8 mb-4">Importancia para el sector empresarial</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                En COPARMEX Manzanillo estamos comprometidos con proporcionar a nuestros socios la información y herramientas necesarias para enfrentar los retos actuales del entorno empresarial. A través de nuestras comisiones de trabajo, generamos análisis, propuestas y acciones que benefician a toda la comunidad empresarial de la región.
              </p>
              <h2 className="text-2xl font-bold text-[#0A1628] mt-8 mb-4">Conclusiones</h2>
              <p className="text-gray-600 leading-relaxed">
                Te invitamos a formar parte de nuestra comunidad y participar activamente en las iniciativas que promovemos. Juntos podemos construir un mejor entorno empresarial para Manzanillo y la región.
              </p>
            </div>

            {/* Tags */}
            <div className="mt-8 pt-8 border-t">
              <div className="flex flex-wrap items-center gap-2">
                <Tag className="h-4 w-4 text-gray-400" />
                <span className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full">{post.category}</span>
                <span className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full">COPARMEX</span>
                <span className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full">Empresarios</span>
              </div>
            </div>

            {/* Related Posts */}
            <div className="mt-12 pt-8 border-t">
              <h3 className="text-xl font-bold text-[#0A1628] mb-6">Artículos relacionados</h3>
              <div className="grid md:grid-cols-2 gap-6">
                {relatedPosts.map((relatedPost) => (
                  <Link
                    key={relatedPost.id}
                    to={`/blog/${relatedPost.id}`}
                    className="group bg-gray-50 rounded-xl overflow-hidden hover:bg-gray-100 transition-colors"
                  >
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={relatedPost.image}
                        alt={relatedPost.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="font-semibold text-[#0A1628] group-hover:text-[#1E40AF] transition-colors">
                        {relatedPost.title}
                      </h4>
                      <span className="text-xs text-gray-400 mt-2 block">{relatedPost.date}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            <div className="mt-12">
              <Link
                to="/blog"
                className="inline-flex items-center gap-2 text-[#1E40AF] font-medium hover:gap-3 transition-all"
              >
                <ArrowLeft className="h-5 w-5" />
                Volver al blog
              </Link>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  // Blog list view
  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-20 bg-[#0A1628] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 bg-blue-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-blue-400 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <nav className="flex items-center gap-2 text-sm text-gray-400 mb-6">
              <Link to="/" className="hover:text-white transition-colors">Inicio</Link>
              <span>/</span>
              <span className="text-blue-400">Blog</span>
            </nav>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Blog Empresarial
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Artículos, análisis y reflexiones sobre el entorno empresarial, elaborados por nuestras comisiones de trabajo.
            </p>
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Featured Post */}
          <div className="mb-12">
            <Link
              to={`/blog/${blogPosts[0].id}`}
              className="group grid md:grid-cols-2 gap-8 bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300"
            >
              <div className="aspect-video md:aspect-auto overflow-hidden">
                <img
                  src={blogPosts[0].image}
                  alt={blogPosts[0].title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-8 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-4">
                  <span className="px-3 py-1 bg-[#1E40AF]/10 text-[#1E40AF] text-sm font-medium rounded-full">
                    {blogPosts[0].category}
                  </span>
                  <span className="text-sm text-gray-400 flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    5 min
                  </span>
                </div>
                <h2 className="text-2xl font-bold text-[#0A1628] mb-4 group-hover:text-[#1E40AF] transition-colors">
                  {blogPosts[0].title}
                </h2>
                <p className="text-gray-600 mb-4">
                  {blogPosts[0].excerpt}
                </p>
                <div className="flex items-center gap-4 text-sm text-gray-400">
                  <span className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    {blogPosts[0].author}
                  </span>
                  <span className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {blogPosts[0].date}
                  </span>
                </div>
              </div>
            </Link>
          </div>

          {/* Posts Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {blogPosts.slice(1).map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.id}`}
                className="group bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300 hover-lift"
              >
                <div className="aspect-video overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-3 py-1 bg-[#1E40AF]/10 text-[#1E40AF] text-xs font-medium rounded-full">
                      {post.category}
                    </span>
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      5 min
                    </span>
                  </div>
                  <h3 className="font-semibold text-[#0A1628] mb-2 group-hover:text-[#1E40AF] transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 text-sm line-clamp-2 mb-4">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <span className="text-xs text-gray-400">{post.author}</span>
                    <span className="text-[#1E40AF] text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                      Leer
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default BlogPage;
